import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  Button,
  ButtonGroup,
  Dropdown,
  Spinner,
  Table,
} from "react-bootstrap";
import { IoIosArrowDropdownCircle } from "react-icons/io";
import { Link } from "react-router-dom";
import Block from "../../components/Block/Block";
import { Icon } from "../../components";
import { getUserList, UserDataBulkUploadAPI } from "../../services/UserService";
import { exportUserList } from "../../services/UserService";
import { toast } from "react-toastify";
import { MediaGroup, MediaText } from "../../components";
import Pagination from "../Constant/Pagination";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";
import { CancelRequest } from "../../services/AxiosInstance";
import { roleDisplayMap } from "../../utilities";
import AddUser from "./AddUser";
import UserImportModal from "./UserImportModal";
import BarLoader from "../../components/Loader/BarLoader";
import { FaRegEdit } from "react-icons/fa";
import EditUser from "./EditUser";
import { roleStyleMap } from "../../utilities";
import { useSelector } from "react-redux";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import { FiUsers } from "react-icons/fi";

function UserListPage() {
  const userRole = useSelector((state) => state.auth.user.role);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showItemPerPage, setShowItemPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({
    query: "",
    is_active: "",
  });
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showEditOffcanvas, setShowEditOffcanvas] = useState(false);
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [expandedRowIndex, setExpandedRowIndex] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  // Status options matching API expectations
  const userStatusOption = [
    { label: "All Statuses", value: "" },
    { label: "Active", value: "ACTIVE" },
    { label: "Inactive", value: "INACTIVE" },
  ];

  // Role options
  const roleOptions = Object.entries(roleDisplayMap)
    .filter(([value]) => value !== "ADMIN")
    .map(([value, label]) => ({
      value,
      label,
    }));

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getUserList(currentPage, showItemPerPage, filter);
      setData(response.data.results);
      setTotal(response.data.count);
    } catch (error) {
      toast.error(error.response?.data?.message);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, showItemPerPage, filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePagination = (page) => {
    setCurrentPage(page.selected + 1);
  };

  const handleSearchChange = (value) => {
    if (value.length > 0) CancelRequest();
    setFilter((prev) => ({
      ...prev,
      query: value,
    }));
    setCurrentPage(1);
  };

  const handleRoleFilter = (role) => {
    setSelectedRole(role);
    setFilter((prev) => ({
      ...prev,
      role: role ? role.value : "",
    }));
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    setFilter((prev) => ({
      ...prev,
      is_active: status ? status.value : "", // Correct parameter name
    }));
    setCurrentPage(1);
  };

  const handleOnUserAdded = (newUser) => {
    setData((prev) => [newUser.data, ...prev]);
    setTotal((prevTotal) => prevTotal + 1);
    setShowOffcanvas(false);
  };

  const handleEditUser = (user) => {
    setSelectedUserData(user);
    setShowEditOffcanvas(true);
  };

  const handleExport = async () => {
    try {
      const response = await exportUserList(filter);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", "export-data.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("User data exported successfully");
    } catch (error) {
      console.error("Export failed", error);
      toast.error("Failed to export user data");
    }
  };

  return (
    <div>
      <Block.Head className="mb-2">
        <Block.HeadBetween>
          <Block.HeadContent>
            <Block.Title tag="h2">Users</Block.Title>
            <nav>
              <ol className="breadcrumb breadcrumb-arrow mb-0">
                <li className="breadcrumb-item">
                  <Link to="/">Dashboard</Link>
                </li>
                {/* <li className="breadcrumb-item"><Link to="/user-manage/user-list">User Manage</Link></li> */}
                <li className="breadcrumb-item active" aria-current="page">
                  Users
                </li>
              </ol>
            </nav>
          </Block.HeadContent>
          {userRole == "ADMIN" && (
            <Block.HeadContent>
              <div className="d-flex flex-wrap align-items-center gap-2">
                {/* Add User (Mobile) */}
                <Button
                  className="d-md-none"
                  size="sm"
                  variant="primary"
                  onClick={() => setShowOffcanvas(true)}
                >
                  <Icon name="plus" />
                  <span>Add</span>
                </Button>

                {/* Add User (Desktop) */}
                <Button
                  className="d-none d-md-inline-flex"
                  variant="outline-primary"
                  onClick={() => setShowOffcanvas(true)}
                  style={{ fontSize: "0.875rem", height: "35px" }}
                >
                  <Icon name="plus" />
                  <span>Add User</span>
                </Button>

                {/* Import */}
                <Button
                  variant="outline-secondary"
                  className="d-none d-md-inline-flex"
                  onClick={() => setShowImportModal(true)}
                  style={{ fontSize: "0.875rem", height: "35px" }}
                >
                  <Icon name="upload" />
                  <span>Import</span>
                </Button>

                {/* Export */}
                <Button
                  variant="outline-secondary"
                  className="d-none d-md-inline-flex"
                  onClick={handleExport}
                  style={{ fontSize: "0.875rem", height: "35px" }}
                >
                  <Icon name="download" />
                  <span>Export</span>
                </Button>
              </div>
            </Block.HeadContent>
          )}
        </Block.HeadBetween>
      </Block.Head>

      <Block className="mb-0">
        <Card>
          <div className="data-table-wrapper">
            <div className="data-table-top py-2 px-3 border-bottom">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <div
                  className="d-flex align-items-center gap-2 fw-semibold fs-5"
                  style={{ color: "#255d5c" }}
                >
                  <FiUsers size={20} />
                  <h2 className="mb-0 fs-5" style={{ color: "#255d5c" }}>
                    User List
                  </h2>
                </div>

                <div className="d-flex align-items-center ms-auto gap-2">
                  <DebounceSmallSearchInput
                    handleSearchChange={handleSearchChange}
                  />

                  <GlobalSearchableDropdown
                    options={userStatusOption}
                    selected={selectedStatus}
                    onSelect={handleStatusFilter}
                    placeholder="Status"
                    styles={{
                      container: (base) => ({
                        ...base,
                        minWidth: "150px",
                        height: "43.5px",
                      }),
                    }}
                  />

                  <GlobalSearchableDropdown
                    options={roleOptions}
                    selected={selectedRole}
                    onSelect={handleRoleFilter}
                    placeholder="Role"
                    styles={{
                      container: (base) => ({
                        ...base,
                        minWidth: "150px",
                        height: "43.5px",
                      }),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            {isLoading ? (
              <div className="text-center my-2">
                <BarLoader color="#255D5C" size={50} />
              </div>
            ) : (
              <>
                <table className="table border-less fs-6 mb-0">
                  <thead className="table-light custom-thead">
                    <tr>
                      <th>Full Name</th>
                      <th>Mobile Number</th>
                      <th>Role</th>
                      <th>Designation</th>
                      <th>Location</th>
                      <th>Department</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          No users found
                        </td>
                      </tr>
                    ) : (
                      data.map((row, index) => {
                        const areas = Array.isArray(row.area)
                          ? row.area
                          : [row.area];
                        const [firstArea, ...remainingAreas] = areas || [];

                        return (
                          <React.Fragment key={index}>
                            <tr className="align-middle">
                              <td>
                                <div className="d-flex flex-column">
                                  <Link to={`/users/${row.id}`}>
                                    <strong>{row.fullname}</strong>
                                  </Link>
                                  {row.email}
                                </div>
                              </td>
                              <td>{row.mobile_number}</td>
                              <td>
                                <span
                                  className={`badge px-2 py-1 fw-medium rounded ${
                                    roleStyleMap[row.role] ||
                                    "bg-light text-dark"
                                  }`}
                                  style={{ fontSize: "0.85rem" }}
                                >
                                  {roleDisplayMap[row.role] || row.role}
                                </span>
                              </td>
                              <td>{row.designation || "-"}</td>
                              <td>{row.location || "-"}</td>
                              <td>{row.department || "-"}</td>
                              <td>
                                <span
                                  className={`badge px-3 py-1 fw-medium ${
                                    row.is_active
                                      ? "bg-success-subtle text-success"
                                      : "bg-danger-subtle text-danger"
                                  }`}
                                >
                                  {row.is_active ? "Active" : "In-Active"}
                                </span>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>

                <div className="p-3">
                  <Pagination
                    currentPage={currentPage}
                    recordsPage={showItemPerPage}
                    dataLength={total}
                    handlePagination={handlePagination}
                  />
                </div>
              </>
            )}
          </div>
        </Card>
      </Block>

      <AddUser
        showOffcanvas={showOffcanvas}
        setShowOffcanvas={setShowOffcanvas}
        onUserAdded={handleOnUserAdded}
      />
      <UserImportModal
        show={showImportModal}
        onClose={() => setShowImportModal(false)}
        onUploadSuccess={fetchData}
      />
      <EditUser
        showEditOffcanvas={showEditOffcanvas}
        setShowEditOffcanvas={setShowEditOffcanvas}
        userData={selectedUserData}
        onUserUpdated={fetchData}
      />
    </div>
  );
}

export default UserListPage;
